import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Shield, Trash2, LogOut, UserPlus, Mail, List, Loader2, Copy, Check,
  Key, Plus, AlertTriangle, Users as UsersIcon, ShieldCheck, QrCode,
  Building2, Save,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, isLoading, logout, authFetch, setup2FA, enable2FA, disable2FA } = useAuth();

  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newDays, setNewDays] = useState(7);
  const [creating, setCreating] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [granting, setGranting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const fetchCodes = useCallback(async () => {
    setLoadingCodes(true);
    try {
      const resp = await authFetch('/api/admin/tester-codes');
      if (resp.ok) {
        const data = await resp.json();
        setCodes(data.codes || []);
      }
    } finally {
      setLoadingCodes(false);
    }
  }, [authFetch]);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const resp = await authFetch('/api/admin/users');
      if (resp.ok) {
        const data = await resp.json();
        setUsers(data.users || []);
      }
    } finally {
      setLoadingUsers(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/admin' } });
      return;
    }
    if (!isAdmin) return;
    fetchCodes();
    fetchUsers();
  }, [isLoading, isAuthenticated, isAdmin, navigate, fetchCodes, fetchUsers]);

  const createCode = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const resp = await authFetch('/api/admin/tester-codes', {
        method: 'POST',
        body: JSON.stringify({ label: newLabel, valid_days: Number(newDays) || 7 }),
      });
      const data = await resp.json();
      if (resp.ok) {
        toast.success(`Code generated: ${data.code}`);
        setNewLabel('');
        setNewDays(7);
        fetchCodes();
      } else {
        toast.error(data.detail || 'Could not create code');
      }
    } finally {
      setCreating(false);
    }
  };

  const revokeCode = async (code) => {
    if (!window.confirm(`Revoke code ${code}?`)) return;
    const resp = await authFetch(`/api/admin/tester-codes/${encodeURIComponent(code)}`, { method: 'DELETE' });
    if (resp.ok) {
      toast.success('Code revoked');
      fetchCodes();
    } else {
      toast.error('Could not revoke code');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const grantAccess = async (e) => {
    e.preventDefault();
    setGranting(true);
    try {
      const resp = await authFetch('/api/admin/grant-access', {
        method: 'POST',
        body: JSON.stringify({ email: grantEmail.trim(), reason: grantReason || 'Manual admin grant' }),
      });
      const data = await resp.json();
      if (resp.ok) {
        toast.success(data.message);
        setGrantEmail('');
        setGrantReason('');
        fetchUsers();
      } else {
        toast.error(data.detail || 'Could not grant access');
      }
    } finally {
      setGranting(false);
    }
  };

  const revokeAccess = async (email) => {
    if (!window.confirm(`Revoke course access for ${email}?`)) return;
    const resp = await authFetch('/api/admin/revoke-access', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    const data = await resp.json();
    if (resp.ok) {
      toast.success('Access revoked');
      fetchUsers();
    } else {
      toast.error(data.detail || 'Could not revoke');
    }
  };

  if (isLoading) {
    return <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" /></div>;
  }

  if (!isAuthenticated) {
    return null; // redirect in progress
  }

  if (!isAdmin) {
    return (
      <div className="max-w-xl mx-auto py-10 text-center">
        <AlertTriangle className="w-12 h-12 text-error mx-auto mb-4" />
        <h1 className="text-2xl font-heading font-bold mb-2">Admin access required</h1>
        <p className="text-text-muted mb-6">This page is for administrators only.</p>
        <Link to="/"><Button>Back to home</Button></Link>
      </div>
    );
  }

  const activeCodes = codes.filter((c) => !c.revoked && !c.used_by);
  const usedCodes = codes.filter((c) => c.used_by);
  const revokedCodes = codes.filter((c) => c.revoked);

  return (
    <div className="max-w-5xl mx-auto fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-text">Admin Panel</h1>
            <p className="text-text-muted text-sm">{user?.email}</p>
          </div>
        </div>
        <Button variant="outline" onClick={logout} data-testid="admin-logout" className="rounded-full">
          <LogOut className="w-4 h-4 mr-2" />Sign out
        </Button>
      </div>

      {/* 2FA setup */}
      <TwoFactorSection
        user={user}
        setup2FA={setup2FA}
        enable2FA={enable2FA}
        disable2FA={disable2FA}
      />

      {/* Business / Legal settings */}
      <LegalSettingsSection authFetch={authFetch} />

      {/* Tester codes */}
      <section className="bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Key className="w-5 h-5 text-primary" />
          <h2 className="font-heading font-bold text-xl">Tester codes</h2>
        </div>
        <p className="text-text-muted text-sm mb-6">
          Single-use codes with an expiry. Each code works for one person; it unlocks the full course for the number of days you set.
        </p>

        <form onSubmit={createCode} className="grid md:grid-cols-[2fr_1fr_auto] gap-3 mb-6">
          <Input
            data-testid="new-code-label"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Label (optional, e.g. 'Family tester')"
          />
          <Input
            data-testid="new-code-days"
            type="number"
            min={1}
            max={365}
            value={newDays}
            onChange={(e) => setNewDays(e.target.value)}
            placeholder="Days"
          />
          <Button type="submit" disabled={creating} data-testid="generate-code" className="rounded-xl">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Generate
          </Button>
        </form>

        <div className="space-y-6">
          <CodeList title={`Active (${activeCodes.length})`} codes={activeCodes} onCopy={copyCode} onRevoke={revokeCode} copiedCode={copiedCode} />
          {usedCodes.length > 0 && <CodeList title={`Used (${usedCodes.length})`} codes={usedCodes} onCopy={copyCode} onRevoke={revokeCode} copiedCode={copiedCode} readOnly />}
          {revokedCodes.length > 0 && <CodeList title={`Revoked (${revokedCodes.length})`} codes={revokedCodes} onCopy={copyCode} onRevoke={revokeCode} copiedCode={copiedCode} readOnly />}
        </div>

        {loadingCodes && <p className="text-text-muted text-sm mt-3">Loading…</p>}
      </section>

      {/* Grant access */}
      <section className="bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-5 h-5 text-primary" />
          <h2 className="font-heading font-bold text-xl">Grant course access</h2>
        </div>
        <p className="text-text-muted text-sm mb-6">
          Give a user permanent access without charging them (e.g. refund case, giveaway). The user must have signed up first.
        </p>
        <form onSubmit={grantAccess} className="grid md:grid-cols-[2fr_2fr_auto] gap-3">
          <Input
            data-testid="grant-email"
            type="email"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            placeholder="user@example.com"
            required
          />
          <Input
            data-testid="grant-reason"
            value={grantReason}
            onChange={(e) => setGrantReason(e.target.value)}
            placeholder="Reason (optional)"
          />
          <Button type="submit" disabled={granting} data-testid="grant-submit" className="rounded-xl">
            {granting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Grant
          </Button>
        </form>
      </section>

      {/* Users */}
      <section className="bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-primary" />
            <h2 className="font-heading font-bold text-xl">Users ({users.length})</h2>
          </div>
          <Button variant="outline" size="sm" onClick={fetchUsers} className="rounded-full">
            <List className="w-4 h-4 mr-2" />Refresh
          </Button>
        </div>
        {loadingUsers ? (
          <p className="text-text-muted">Loading…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="text-left text-text-muted border-b">
                  <th className="py-2">Email</th>
                  <th className="py-2">Name</th>
                  <th className="py-2">Role</th>
                  <th className="py-2">Verified</th>
                  <th className="py-2">Access</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b last:border-b-0">
                    <td className="py-2 pr-4">{u.email}</td>
                    <td className="py-2 pr-4">{u.first_name}</td>
                    <td className="py-2 pr-4">
                      {u.role === 'admin' ? <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-semibold">admin</span> : u.role}
                    </td>
                    <td className="py-2 pr-4">{u.email_verified ? '✓' : '—'}</td>
                    <td className="py-2 pr-4">
                      {u.has_access ? (
                        <span className="bg-success/10 text-success px-2 py-0.5 rounded-full text-xs font-semibold">
                          {u.access_source}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="py-2">
                      {u.has_access && u.role !== 'admin' && (
                        <Button size="sm" variant="outline" onClick={() => revokeAccess(u.email)} className="rounded-full h-7 px-3 text-xs">
                          <Trash2 className="w-3 h-3 mr-1" />Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function CodeList({ title, codes, onCopy, onRevoke, copiedCode, readOnly }) {
  if (codes.length === 0) return null;
  return (
    <div>
      <h3 className="font-semibold text-text mb-2 text-sm uppercase tracking-wider">{title}</h3>
      <ul className="space-y-2">
        {codes.map((c) => (
          <li key={c.code} className="flex items-center justify-between gap-3 bg-stone-50 border border-stone-200 rounded-xl p-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <code className="bg-white border border-stone-200 rounded px-2 py-0.5 font-mono text-sm" data-testid={`code-${c.code}`}>{c.code}</code>
                {c.label && <span className="text-text-muted text-xs">{c.label}</span>}
              </div>
              <div className="text-xs text-text-muted mt-1">
                Expires {new Date(c.expires_at).toLocaleDateString('en-AU')} · {c.valid_days}d
                {c.used_by && ' · used'}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={() => onCopy(c.code)} className="h-8 px-3 rounded-full">
                {copiedCode === c.code ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              </Button>
              {!readOnly && (
                <Button size="sm" variant="outline" onClick={() => onRevoke(c.code)} className="h-8 px-3 rounded-full text-error border-error/30 hover:bg-error/10">
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TwoFactorSection({ user, setup2FA, enable2FA, disable2FA }) {
  const [setupData, setSetupData] = useState(null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const enabled = !!user?.totp_enabled;

  const startSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await setup2FA();
      setSetupData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await enable2FA(otp.trim());
      toast.success('Two-factor authentication enabled');
      setSetupData(null);
      setOtp('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmDisable = async () => {
    if (!window.confirm('Disable two-factor authentication for this admin account? This weakens security.')) return;
    setLoading(true);
    try {
      await disable2FA();
      toast.success('Two-factor authentication disabled');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8 mb-8" data-testid="admin-2fa-section">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-bold text-xl">Two-factor authentication</h2>
        {enabled && (
          <span className="ml-2 bg-success/10 text-success px-2 py-0.5 rounded-full text-xs font-semibold">Enabled</span>
        )}
      </div>
      <p className="text-text-muted text-sm mb-4">
        Add an extra layer of security to your admin account using an authenticator app (Google Authenticator, Authy, 1Password).
      </p>

      {enabled && !setupData && (
        <Button
          variant="outline"
          onClick={confirmDisable}
          disabled={loading}
          data-testid="admin-2fa-disable"
          className="rounded-xl text-error border-error/30 hover:bg-error/10"
        >
          Disable two-factor
        </Button>
      )}

      {!enabled && !setupData && (
        <Button onClick={startSetup} disabled={loading} data-testid="admin-2fa-setup" className="rounded-xl bg-primary text-white">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
          Set up two-factor
        </Button>
      )}

      {setupData && (
        <div className="mt-4 bg-stone-50 border border-stone-200 rounded-xl p-5">
          <ol className="space-y-4">
            <li>
              <p className="font-semibold text-text mb-2">1. Scan this QR with your authenticator app</p>
              <div className="bg-white p-4 rounded-lg border border-stone-200 inline-block">
                <img src={setupData.qr_data_url} alt="2FA QR code" width={200} height={200} data-testid="admin-2fa-qr" />
              </div>
              <p className="text-xs text-text-muted mt-2">
                Can't scan? Enter this code manually: <code className="bg-white px-2 py-0.5 rounded border border-stone-200 font-mono text-xs">{setupData.secret}</code>
              </p>
            </li>
            <li>
              <p className="font-semibold text-text mb-2">2. Enter the 6-digit code from your app</p>
              <form onSubmit={confirmEnable} className="flex gap-2 flex-wrap">
                <Input
                  data-testid="admin-2fa-code"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="123456"
                  maxLength={10}
                  className="font-mono tracking-widest w-32"
                  autoFocus
                />
                <Button type="submit" disabled={loading} data-testid="admin-2fa-confirm" className="rounded-xl bg-primary text-white">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Enable
                </Button>
                <Button type="button" variant="outline" onClick={() => { setSetupData(null); setOtp(''); setError(null); }}>
                  Cancel
                </Button>
              </form>
            </li>
          </ol>
          {error && <p className="text-error text-sm mt-3" data-testid="admin-2fa-error">{error}</p>}
        </div>
      )}
    </section>
  );
}

function LegalSettingsSection({ authFetch }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ business_name: '', abn: '', contact_email: '' });
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await authFetch('/api/settings/legal');
        if (resp.ok) {
          const data = await resp.json();
          setForm({
            business_name: data.business_name || '',
            abn: data.abn || '',
            contact_email: data.contact_email || '',
          });
          setUpdatedAt(data.updated_at);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [authFetch]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const resp = await authFetch('/api/admin/settings/legal', {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      const data = await resp.json();
      if (resp.ok) {
        toast.success('Business settings saved. Terms, Privacy and Refund pages will reflect the new values.');
        setUpdatedAt(data.settings?.updated_at);
      } else {
        toast.error(data?.detail || 'Could not save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8 mb-8" data-testid="admin-legal-settings">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-bold text-xl">Business details</h2>
      </div>
      <p className="text-text-muted text-sm mb-6">
        These values appear on your Terms of Service, Privacy Policy, and Refund Policy pages. Update here once and all three pages will refresh automatically.
      </p>
      {loading ? (
        <p className="text-text-muted">Loading…</p>
      ) : (
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-text mb-1">Business name</label>
            <Input
              data-testid="legal-business-name"
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              placeholder="e.g. Bellarine Tech Assist"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-text mb-1">ABN</label>
            <Input
              data-testid="legal-abn"
              value={form.abn}
              onChange={(e) => setForm({ ...form, abn: e.target.value })}
              placeholder="e.g. 51 387 096 106"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-text mb-1">Contact email</label>
            <Input
              data-testid="legal-contact-email"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="e.g. info@bellarinetechassist.com.au"
              required
            />
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
            {updatedAt && (
              <p className="text-xs text-text-muted">Last updated {new Date(updatedAt).toLocaleString('en-AU')}</p>
            )}
            <Button type="submit" disabled={saving} data-testid="legal-save" className="rounded-xl bg-primary text-white ml-auto">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
